import { SEQUENCE_END_DETAILS, SEQUENCE_END_REASONS, violationSchema, type CallSequenceView, type LeadNextStep, type SequenceViolationView } from "@gioia365/contracts";
import type { MessageKey } from "@gioia365/i18n";
import { z } from "zod";
import type { IconName } from "../../design/components/Icon";
import type { Tone } from "../../design/components/tone";
import { formatNumericDateTime } from "../../i18n/format";
import type { Translate } from "../../i18n/locale";
import { dayWords } from "../../routes/settings/call-sequence-draft";

// Le parole della sequenza di chiamate (piano M5, Task 12; D8, D16): la riga in cima alla sezione della scheda (`sequenceLine`
// del mock-up), il primo motivo per cui non è rispettata, le caselle, «Prossimo passo» e i titoli degli eventi della Timeline.
// Funzioni pure sopra la vista del server (`callSequenceViewSchema`): il giudizio e i motivi li scrive il server, qui si dicono.

type BoxState = CallSequenceView["calls"][number]["state"];
type EndReason = NonNullable<CallSequenceView["endReason"]>;
type EndDetail = NonNullable<CallSequenceView["endDetail"]>;
type StatusIcon = Extract<IconName, "check" | "clock" | "minus" | "alert">;

/** La legenda: lo stato di una casella a parole. */
export const BOX_STATE_WORDS = {
  due: "callSequence.legend.due",
  now: "callSequence.legend.now",
  done: "callSequence.legend.done",
  answered: "callSequence.legend.answered",
  not_needed: "callSequence.legend.not_needed",
  not_made: "callSequence.legend.not_made",
  stopped: "callSequence.legend.stopped",
} as const satisfies Record<BoxState, MessageKey>;

/** Il motivo di una fine, in fondo a «Sequenza finita: …» nella Timeline e in «fermata al tentativo N · …» sulla scheda. */
const END_WORDS = {
  booked: "callSequence.endWords.booked",
  answered: "callSequence.endWords.answered",
  answered_callback: "callSequence.endWords.answered_callback",
  answered_whatsapp: "callSequence.endWords.answered_whatsapp",
  answered_manually: "callSequence.endWords.answered_manually",
  wrong_number: "callSequence.endWords.wrong_number",
  blocked_status: "callSequence.endWords.blocked_status",
  exhausted: "callSequence.endWords.exhausted",
  stopped_manually: "callSequence.endWords.stopped_manually",
  archived: "callSequence.endWords.archived",
  merged: "callSequence.endWords.merged",
} as const satisfies Record<EndReason, MessageKey>;

/** Le tre risposte di «Come ha risposto il Lead?». */
export const ANSWERED_HOW = {
  whatsapp: "callSequence.answeredHow.whatsapp",
  called_back: "callSequence.answeredHow.called_back",
  other: "callSequence.answeredHow.other",
} as const satisfies Record<EndDetail, MessageKey>;

/** Come ha risposto, dentro una frase: «Il Lead ha risposto (ha richiamato) · segnato a mano». */
const ANSWERED_HOW_WORDS = {
  whatsapp: "callSequence.answeredHowWords.whatsapp",
  called_back: "callSequence.answeredHowWords.called_back",
  other: "callSequence.answeredHowWords.other",
} as const satisfies Record<EndDetail, MessageKey>;

/** «nessuna chiamata», «una chiamata», «3 chiamate» (`callsWord` del mock-up). */
export function callsWord(t: Translate, count: number): string {
  if (count === 0) return t("callSequence.callsWord.none");
  if (count === 1) return t("callSequence.callsWord.one");
  return t("callSequence.callsWord.many", { count });
}

/** «45 minuti», «un'ora», «un'ora e 15 minuti», «2 ore» (`durationWords` del mock-up): minuti di lavoro. */
export function durationWords(t: Translate, minutes: number): string {
  const whole = Math.max(0, Math.round(minutes));
  const minutesText = (count: number) => (count === 1 ? t("callSequence.duration.minute") : t("callSequence.duration.minutes", { count }));
  const hours = Math.floor(whole / 60);
  const rest = whole % 60;
  if (hours === 0) return minutesText(whole);
  const hoursText = hours === 1 ? t("callSequence.duration.hour") : t("callSequence.duration.hours", { count: hours });
  return rest === 0 ? hoursText : t("callSequence.duration.withMinutes", { hours: hoursText, minutes: minutesText(rest) });
}

const HALF_DAY_KEY = /^(\d{4}-\d{2}-\d{2}):([mp])$/;

/** Una mezza giornata del server (`AAAA-MM-GG:m|p`) come «giovedì 08/10 pomeriggio»; una forma che non conosce resta com'è. */
export function halfDayWords(t: Translate, key: string): string {
  const match = HALF_DAY_KEY.exec(key);
  if (!match?.[1]) return key;
  const halfDay = match[2] === "m" ? t("callSequence.halfDay.morning") : t("callSequence.halfDay.afternoon");
  return t("callSequence.halfDayWords", { day: dayWords(t, match[1]), halfDay });
}

/** Il primo motivo per cui la sequenza non è rispettata, con le parole del mock-up (`firstCallLate`, `missedHalf`). */
export function violationText(t: Translate, violation: SequenceViolationView): string {
  switch (violation.code) {
    case "first_call_late":
      return violation.afterOpening === null
        ? t("callSequence.reasons.first_call_late", { duration: durationWords(t, violation.minutes) })
        : t("callSequence.reasons.first_call_late_after_opening", { duration: durationWords(t, violation.minutes), time: violation.afterOpening });
    case "first_call_missing":
      return violation.sinceHalfDay === null
        ? t("callSequence.reasons.first_call_missing", { duration: durationWords(t, violation.minutes) })
        : t("callSequence.reasons.first_call_missing_since", { halfDay: halfDayWords(t, violation.sinceHalfDay) });
    case "attempt_short":
      return t("callSequence.reasons.attempt_short", { n: violation.attempt, calls: callsWord(t, violation.made), required: violation.required });
    case "half_day_missed":
      return t("callSequence.reasons.half_day_missed", { halfDay: halfDayWords(t, violation.halfDay) });
  }
}

/** Il giudizio, «rispettata ✓» o «non rispettata: …»; `null` finché non c'è niente da giudicare. */
function verdictOf(t: Translate, sequence: CallSequenceView): string | null {
  if (sequence.compliance === "respected") return t("callSequence.respected");
  if (sequence.compliance === "violated" && sequence.violation !== null) return t("callSequence.violated", { reason: violationText(t, sequence.violation) });
  return null;
}

/** La riga di una sequenza finita, prima del giudizio. */
function endLine(t: Translate, sequence: CallSequenceView): string {
  const n = sequence.currentAttempt;
  switch (sequence.endReason) {
    case "booked":
      return t("callSequence.endWords.booked");
    case "stopped_manually":
      return t("callSequence.endStopped");
    case "answered":
      return t("callSequence.endAnswered", { n });
    case "answered_callback":
      return t("callSequence.endCalledBack");
    case "answered_whatsapp":
      return t("callSequence.endWhatsapp");
    case "answered_manually":
      if (sequence.endDetail === "whatsapp") return t("callSequence.endWhatsapp");
      if (sequence.endDetail === "called_back") return t("callSequence.endCalledBack");
      return t("callSequence.endManualOther");
    case "wrong_number":
    case "blocked_status":
      return t("callSequence.endBlocked", { n, reason: t(END_WORDS[sequence.endReason]) });
    case "archived":
      return t("callSequence.endArchived");
    case "merged":
      return t("callSequence.endMerged");
    case "exhausted":
      return t("callSequence.endExhausted", { total: sequence.total });
    case null:
      return t("callSequence.title");
  }
}

/** Le partenze che sono una ripartenza: «Riparti con la sequenza», il rientro dopo una sequenza esaurita, la ripartenza da sola (SEQ-14). */
const RESTARTS: ReadonlySet<CallSequenceView["startReason"]> = new Set(["restarted_manually", "restarted_on_reentry", "restarted_automatically"]);

export type SequenceLine = { text: string; tone: Tone; icon: StatusIcon };

/**
 * La riga in cima alla sezione (`sequenceLine` del mock-up): in corso «Sequenza: tentativo N di 6 · …» con il giudizio, oppure
 * «Lead nuovo», «appena iniziata», «ripartita da capo» finché non c'è; finita, il perché, con il giudizio dopo se c'è. Il tono
 * segue il giudizio; il testo dice tutto anche senza il colore.
 */
export function sequenceLine(t: Translate, sequence: CallSequenceView): SequenceLine {
  const verdict = verdictOf(t, sequence);
  const violated = sequence.compliance === "violated" && verdict !== null;
  if (sequence.state === "open") {
    const restarted = RESTARTS.has(sequence.startReason);
    const started = sequence.madeInCurrent > 0 || sequence.calls.some((box) => box.callId !== null);
    const judged = verdict ?? t(restarted ? "callSequence.judgedRestarted" : started ? "callSequence.judgedStarted" : "callSequence.judgedNew");
    const text = t("callSequence.lineAttempt", { n: sequence.currentAttempt, total: sequence.total, judged });
    if (violated) return { text, tone: "danger", icon: "alert" };
    return verdict === null ? { text, tone: "neutral", icon: "clock" } : { text, tone: "success", icon: "check" };
  }
  const line = endLine(t, sequence);
  const text = verdict === null ? line : t("callSequence.lineWithVerdict", { line, verdict });
  if (violated) return { text, tone: "danger", icon: "alert" };
  return { text, tone: "neutral", icon: "minus" };
}

/**
 * «Chiamata della sequenza · tentativo 3 di 6 · giovedì 08/10 pomeriggio», oppure «· adesso» quando è già dovuto; il richiamo
 * aperto (piano M5, Task 16) «Richiamo · 08/10/2026 10:00», nel fuso dell'azienda.
 */
export function nextStepText(t: Translate, step: LeadNextStep, timeZone: string): string {
  if (step.kind === "callback") return t("callSequence.nextStepCallback", { when: formatNumericDateTime(timeZone, step.dueAt) });
  const when = step.dueNow ? t("callSequence.whenNow") : halfDayWords(t, step.halfDay);
  return t("callSequence.nextStepAttempt", { n: step.attempt, total: step.total, when });
}

const endReasonSchema = z.enum(SEQUENCE_END_REASONS);
const endDetailSchema = z.enum(SEQUENCE_END_DETAILS);
const countSchema = z.number().int().min(0);

/** «Il Lead ha risposto (ha richiamato) · segnato a mano»; un «come» che non conosce vale «in un altro modo». */
function answeredManuallyTitle(t: Translate, how: unknown): string {
  const detail = endDetailSchema.safeParse(how);
  return t("timeline.event.sequenceAnsweredManually", { how: t(ANSWERED_HOW_WORDS[detail.success ? detail.data : "other"]) });
}

/**
 * Il titolo e l'icona di un evento della sequenza nella Timeline (D18; ruling Q34: ogni fine è un `sequence.ended` con il
 * motivo, `sequence.stopped` e `sequence.answered_manually` restano riservati e si leggono lo stesso); `null` per un altro tipo
 * o per un evento che non porta ciò che serve (la Timeline lo mostra come «Attività registrata»).
 */
export function sequenceEventTitle(t: Translate, type: string, payload: Record<string, unknown>): { icon: IconName; title: string } | null {
  switch (type) {
    case "sequence.started":
      return { icon: "play", title: t("timeline.event.sequenceStarted") };
    case "sequence.restarted": {
      // La ripartenza da sola dopo N giorni lavorativi senza contatto (SEQ-14) dice i giorni; ogni altra ripartenza, o un payload senza
      // i giorni, «da capo».
      const days = countSchema.safeParse(payload.afterWorkingDays);
      if (payload.reason === "restarted_automatically" && days.success) return { icon: "play", title: t("timeline.event.sequenceRestartedAutomatically", { n: days.data }) };
      return { icon: "play", title: t("timeline.event.sequenceRestarted") };
    }
    case "sequence.attempt_closed": {
      const attempt = countSchema.safeParse(payload.attempt);
      const made = countSchema.safeParse(payload.made);
      const required = countSchema.safeParse(payload.required);
      if (!attempt.success || !made.success || !required.success) return null;
      return { icon: "check", title: t("timeline.event.sequenceAttemptClosed", { n: attempt.data, calls: callsWord(t, made.data), required: required.data }) };
    }
    case "sequence.violated": {
      const violation = violationSchema.safeParse(payload.violation);
      return violation.success ? { icon: "alert", title: t("timeline.event.sequenceViolated", { reason: violationText(t, violation.data) }) } : null;
    }
    case "sequence.ended": {
      const reason = endReasonSchema.safeParse(payload.endReason);
      if (!reason.success) return null;
      if (reason.data === "stopped_manually") return { icon: "stop", title: t("timeline.event.sequenceStopped") };
      if (reason.data === "answered_manually") return { icon: "phone", title: answeredManuallyTitle(t, payload.endDetail) };
      return { icon: "stop", title: t("timeline.event.sequenceEnded", { reason: t(END_WORDS[reason.data]) }) };
    }
    case "sequence.stopped":
      return { icon: "stop", title: t("timeline.event.sequenceStopped") };
    case "sequence.answered_manually":
      return { icon: "phone", title: answeredManuallyTitle(t, payload.how ?? payload.endDetail) };
    default:
      return null;
  }
}
